import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import {
  BarChartOutlined,
  BookOutlined,
  CloseOutlined,
  CommentOutlined,
  EyeOutlined,
  HeartOutlined,
  LineChartOutlined,
  ShareAltOutlined,
  TagsOutlined,
  ThunderboltOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  Tooltip as AntTooltip,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  message,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import Router from "../..";
import { useUserStore } from "../../../stores/userStore";
import { CreatorAiAnalysisPanel } from "../search/InfluencerAiAnalysis";
import {
  useAddCategory,
  useDeleteCategory,
  useSocialData,
} from "../../hooks/useSocialData";
import { CATEGORY_MAPPING, CATEGORY_STYLES } from "../../utils/categories";
import { formatCnt } from "../../utils/formatCnt";
import { getNiceDomain, getNiceStep } from "../../utils/chartScale";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { FaRegQuestionCircle } from "react-icons/fa";

dayjs.extend(isoWeek);

const { Title, Paragraph } = Typography;

// 날짜 형식 변환 함수 (YYYY-MM-DD -> M/D)
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  } catch (e) {
    return dateStr;
  }
};

const formatWeek = (weekStr) => {
  if (!weekStr) return "";

  const match = weekStr.match(/(\d{4})-W(\d+)/);
  if (!match) return weekStr;

  const year = Number(match[1]);
  const week = Number(match[2]);

  return dayjs(`${year}-01-04`).isoWeek(week).startOf("isoWeek").format("M/D");
};

// metrics 배열을 차트용 데이터로 변환
const prepareMetricsTrends = (metrics) => {
  if (!metrics || metrics.length === 0)
    return {
      followers: [],
      newFollowers: [],
      lostFollowers: [],
      likes: [],
      comments: [],
      shares: [],
      videoViews: [],
      uniqueVideoViews: [],
      profileViews: [],
      dailyNewFollowers: [],
      dailyTotalFollowers: [],
    };

  const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));

  return {
    followers: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.followers_count || 0,
    })),
    newFollowers: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.daily_new_followers || 0,
    })),
    lostFollowers: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.daily_lost_followers || 0,
    })),
    likes: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.likes || 0,
    })),
    comments: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.comments || 0,
    })),
    shares: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.shares || 0,
    })),
    videoViews: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.video_views || 0,
    })),
    uniqueVideoViews: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.unique_video_views || 0,
    })),
    profileViews: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.profile_views || 0,
    })),
    dailyNewFollowers: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.daily_new_followers || 0,
    })),
    dailyTotalFollowers: sorted.map((m) => ({
      week: formatDate(m.date),
      value: m.daily_total_followers || 0,
    })),
  };
};

// snapshot 차트용 데이터로 변환 (현재 임시로 사용 추후 모두 metrics로 교체 예정)
const prepareSnapshotTrends = (snapshots) => {
  if (!snapshots || snapshots.length === 0)
    return {
      followers: [],
      likes: [],
      videos: [],
      avgViews: [],
      avgLikes: [],
      avgComments: [],
      avgCollects: [],
      avgShares: [],
    };

  const sorted = [...snapshots].sort((a, b) =>
    a.snapshot_week.localeCompare(b.snapshot_week)
  );

  return {
    followers: sorted
      .filter(
        (snap) =>
          snap.follower_count !== null && snap.follower_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.follower_count) || 0,
      })),
    likes: sorted
      .filter(
        (snap) => snap.like_count !== null && snap.like_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.like_count) || 0,
      })),
    videos: sorted
      .filter(
        (snap) => snap.video_count !== null && snap.video_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.video_count) || 0,
      })),
    avgViews: sorted
      .filter(
        (snap) =>
          snap.average_views !== null && snap.average_views !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_views) || 0,
      })),
    avgLikes: sorted
      .filter(
        (snap) =>
          snap.average_likes !== null && snap.average_likes !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_likes) || 0,
      })),
    avgComments: sorted
      .filter(
        (snap) =>
          snap.average_comments !== null && snap.average_comments !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_comments) || 0,
      })),
    avgCollects: sorted
      .filter(
        (snap) =>
          snap.average_collects !== null && snap.average_collects !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_collects) || 0,
      })),
    avgShares: sorted
      .filter(
        (snap) =>
          snap.average_shares !== null && snap.average_shares !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_shares) || 0,
      })),
  };
};

// 참여율 계산 함수
const calculateEngagementRate = (tiktokData) => {
  const influencerInfo = tiktokData?.influencer_info ?? tiktokData;
  if (!influencerInfo || !influencerInfo.follower_count) return 0;

  const followerCount = parseInt(influencerInfo.follower_count) || 0;
  const avgLikes = parseFloat(influencerInfo.average_likes) || 0;
  const avgComments = parseFloat(influencerInfo.average_comments) || 0;
  const avgShares = parseFloat(influencerInfo.average_shares) || 0;

  const totalEngagement = avgLikes + avgComments + avgShares;

  if (followerCount === 0) return 0;
  return parseFloat(((totalEngagement / followerCount) * 100).toFixed(2));
};

const getAverageMetricTooltip = (label) =>
  `최근 10개 영상 기준 평균 ${label}입니다.`;

const renderLabelWithTooltip = (label, tooltipText) => (
  <div className="inline-flex items-center gap-1.5">
    <span className="text-[13px]">{label}</span>
    <AntTooltip title={tooltipText}>
      <span className="inline-flex items-center justify-center text-[#8C8C8C] transition-colors hover:text-[#595959]">
        <FaRegQuestionCircle className="text-[13px]" />
      </span>
    </AntTooltip>
  </div>
);

export const TiktokTabContent = () => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [tiktokRegisterLoading, setTiktokRegisterLoading] = useState(false);
  const [selectedView, setSelectedView] = useState("channel-data");
  const { userInfo } = useUserStore();
  const { data: tiktokData, isLoading } = useSocialData(
    "tiktok",
    userInfo?.type_info?.tiktok_influencer_id
  );
  const addCategoryMutation = useAddCategory();
  const deleteCategoryMutation = useDeleteCategory();

  const influencerInfo = tiktokData?.influencer_info ?? {};
  const categories = tiktokData?.category ?? [];
  const metricsData = influencerInfo?.metrics ?? [];
  const mtrends = prepareMetricsTrends(influencerInfo?.metrics);
  const trends = prepareSnapshotTrends(tiktokData?.snapshot);
  const recentVideos = influencerInfo?.recent_videos || [];
  const engagedAudienceTrends = mtrends.profileViews;
  const appDownloadClickTrends = mtrends.uniqueVideoViews;

  const isValidAudience = (data) =>
    Array.isArray(data) && data.length > 0 && data[0] !== "null";

  const hasAudienceData =
    isValidAudience(tiktokData?.influencer_info?.audience_ages) ||
    isValidAudience(tiktokData?.influencer_info?.audience_genders) ||
    isValidAudience(tiktokData?.influencer_info?.audience_countries) ||
    isValidAudience(tiktokData?.influencer_info?.audience_cities);

  const TIKTOK_AUTH_URL =
    "https://www.tiktok.com/v2/auth/authorize?client_key=7547616803401236496&scope=user.info.basic%2Cbiz.creator.info%2Cbiz.creator.insights%2Cvideo.list%2Ctto.campaign.link%2Cbiz.brand.insights%2Ccomment.list%2Cuser.info.username%2Cuser.info.stats%2Cuser.info.profile%2Cuser.account.type%2Cuser.insights%2Cvideo.insights%2Ccomment.list.manage%2Cvideo.publish%2Cvideo.upload%2Cbiz.spark.auth%2Cdiscovery.search.words&response_type=code&redirect_uri=https%3A%2F%2Fapp.surfingcre.com%2Fauth-success%2F";

  // 팝업 인증 처리 함수
  const handleTiktokAuth = () => {
    const popup = window.open(
      TIKTOK_AUTH_URL,
      "tiktokAuthPopup",
      "width=500,height=700"
    );

    let handled = false;

    const timer = setInterval(async () => {
      try {
        if (!popup || popup.closed) {
          clearInterval(timer);
          return;
        }
        const url = popup.location.href;
        if (
          !handled &&
          url.startsWith("https://app.surfingcre.com/auth-success/")
        ) {
          handled = true;
          const params = new URL(url).searchParams;
          const code = params.get("code");
          if (code) {
            clearInterval(timer);
            setTiktokRegisterLoading(true);
            const registered = await Router.Api.registerTiktokCode(
              decodeURIComponent(code)
            );
            if (registered == null) {
              setTiktokRegisterLoading(false);
              return;
            }
            popup.close();
            window.location.reload();
          }
        }
      } catch (e) {
        console.error(e);
      }
    }, 500);
  };

  // 카테고리 저장
  const addCategories = () => {
    if (selectedCategories.length === 0) {
      message.warning("최소 1개 이상의 카테고리를 선택해주세요.");
      return;
    }

    addCategoryMutation.mutate(
      { categories: selectedCategories, social: "tiktok" },
      {
        onSuccess: () => {
          setSelectedCategories([]);
        },
      }
    );
  };

  // 카테고리 삭제
  const deleteCategories = (category, social) => {
    deleteCategoryMutation.mutate({ categories: [category], social });
  };

  // 섹션 제목 컴포넌트
  const SectionTitle = ({ children }) => (
    <h3 className="mb-5 text-[18px] font-semibold text-[#262626] pb-3 border-b-2 border-[#F0F0F0]">
      {children}
    </h3>
  );

  // 통계 카드 컴포넌트
  const TrendCard = ({
    title,
    titleText = typeof title === "string" ? title : "",
    data,
    showAllTicks = false,
  }) => {
    const validData = data.filter(
      (d) => d.week && d.week !== "undefined/undefined"
    );

    if (validData.length === 0) {
      return (
        <Card
          styles={{
            body: {
              padding: "20px",
              background: "#FAFAFA",
              borderRadius: "8px",
            },
          }}
          className="rounded-[8px] border border-[#E8E8E8]"
        >
          <div className="mb-4">
            <span className="text-[14px] font-semibold text-[#262626]">
              {title}
            </span>
          </div>
          <Empty
            description="데이터 없음"
            style={{ padding: "50px 0" }}
            imageStyle={{ height: 60 }}
          />
        </Card>
      );
    }

    const values = validData.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);

    const [niceMin, niceMax] = getNiceDomain(min, max, 4);
    const yStep = getNiceStep(min, max, 4);

    const ticks = [];
    for (let v = niceMin; v <= niceMax; v += yStep) ticks.push(v);

    const gradientId = `gradient-${(titleText || "trend").replace(
      /[^a-zA-Z0-9]/g,
      ""
    )}`;

    const MAX_X_TICKS = 5;
    const xTickLabels = validData.map((d) => d.week);

    const xStep = Math.ceil(xTickLabels.length / MAX_X_TICKS);

    const xTicks = showAllTicks
      ? xTickLabels
      : xTickLabels.filter((_, i) => i % xStep === 0);

    if (!showAllTicks && xTicks.at(-1) !== xTickLabels.at(-1)) {
      xTicks.push(xTickLabels.at(-1));
    }

    return (
      <Card
        styles={{
          body: {
            padding: "20px",
            background: "#FAFAFA",
            borderRadius: "8px",
          },
        }}
        className="rounded-[8px] border border-[#E8E8E8]"
      >
        <div className="mb-4">
          <span className="text-[14px] font-semibold text-[#262626]">
            {title}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={validData}
            margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F9A43F" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#F9A43F" stopOpacity={0.05} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: "#666" }}
              axisLine={{ stroke: "#D9D9D9" }}
              tickLine={{ stroke: "#D9D9D9" }}
              ticks={xTicks}
              tickMargin={6}
              preserveStartEnd
              padding={{ left: 0, right: 0 }}
            />
            <YAxis
              domain={[niceMin, niceMax]}
              ticks={ticks}
              tick={{ fontSize: 11, fill: "#666" }}
              tickFormatter={(value) => formatCnt(value)}
              axisLine={false}
              tickLine={false}
              width={60}
            />
            <RechartsTooltip
              formatter={(value) => [formatCnt(value), titleText]}
              labelStyle={{ fontSize: 12, fontWeight: 600 }}
              contentStyle={{
                fontSize: 11,
                borderRadius: "6px",
                border: "1px solid #E8E8E8",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "8px 12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#F9A43F"
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  return (
    <div className="w-full max-w-[1212.5px] p-4 bg-white rounded-tr-[10px] rounded-bl-[10px] rounded-br-[10px] border border-[#d9d9d9] shadow-[0_2px_6px_rgba(0,0,0,0.1)] overflow-x-hidden">
      {isLoading || tiktokRegisterLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {userInfo?.type_info?.tiktok_influencer_id === null && (
            <div className="flex flex-col items-center py-10 px-5 text-center">
              {/* 1. 인증 유도 메시지 */}
              <Space direction="vertical" size="large" className="w-full">
                <div>
                  <Title
                    level={3}
                    className="mb-2 text-[#262626] text-[20px] sm:text-[24px] md:text-[28px] lg:text-[32px]"
                  >
                    틱톡 계정을 연동해주세요
                  </Title>
                  <Paragraph className="text-[14px] sm:text-[16px] md:text-[18px] text-[#595959] mb-0">
                    틱톡 계정을 연동하여 데이터를 확인할 수 있습니다
                  </Paragraph>
                </div>

                {/* 4. 시각적 개선 - 버튼 스타일링 */}
                <Button
                  type="primary"
                  size="large"
                  onClick={handleTiktokAuth}
                  className="bg-[#69C9D0] border-[#69C9D0] hover:!bg-[#69C9D0] h-12 sm:h-14 md:h-16 text-[16px] sm:text-[18px] md:text-[20px] font-semibold px-8 sm:px-10 md:px-12 rounded-lg shadow-[0_2px_8px_rgba(105,201,208,0.3)]"
                >
                  틱톡 인증하기
                </Button>

                <Divider className="my-8 text-[14px] sm:text-[16px] md:text-[18px]">
                  인증 후 이용 가능한 기능
                </Divider>

                {/* 3. 인증의 이점 강조 */}
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                      styles={{ body: { padding: "20px" } }}
                    >
                      <BarChartOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#69C9D0] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        채널 통계 확인
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        팔로워, 조회수, 좋아요 등
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                      styles={{ body: { padding: "20px" } }}
                    >
                      <TagsOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#69C9D0] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        카테고리 설정
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        전문 분야 선택
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                    >
                      <LineChartOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#69C9D0] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        데이터 분석
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        주간 추이, 인게이지먼트 등
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Space>
            </div>
          )}

          {/* userInfo?.type_info?.tiktok_influencer_id 가 있으면 표시 */}
          {userInfo?.type_info?.tiktok_influencer_id && tiktokData && (
            <>
              <div className="mb-6">
                <Segmented
                  value={selectedView}
                  onChange={setSelectedView}
                  options={[
                    { label: "채널 데이터", value: "channel-data" },
                    {
                      label: "크리에이터 분석",
                      value: "creator-analysis",
                    },
                  ]}
                />
              </div>

              {selectedView === "channel-data" ? (
                <>
                  <Card
                    className="mb-6 rounded-xl border border-[#E8E8E8] shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
                    styles={{ body: { padding: "24px" } }}
                  >
                    {/* 카테고리 선택 섹션 */}
                    <div
                      className={`w-full ${
                        categories.length > 0 ? "mb-6" : "mb-0"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <TagsOutlined className="text-[18px] text-[#69C9D0]" />
                        <label className="text-[16px] font-semibold text-[#262626] m-0">
                          카테고리 선택
                        </label>
                      </div>
                      <div className="flex gap-3 items-start w-full">
                        <Select
                          mode="multiple"
                          style={{ flex: 1, width: "100%" }}
                          placeholder="카테고리를 선택하세요"
                          value={selectedCategories}
                          onChange={setSelectedCategories}
                          options={Object.entries(CATEGORY_MAPPING).map(
                            ([value, label]) => {
                              const savedCategories = categories;

                              const isDisabled =
                                savedCategories.includes(value);
                              return {
                                value,
                                label,
                                disabled: isDisabled,
                              };
                            }
                          )}
                        />
                        <Button
                          type="primary"
                          onClick={addCategories}
                          className="flex-shrink-0 bg-[#69C9D0] hover:!bg-[#69C9D0] hover:!border-[#69C9D0]"
                        >
                          추가
                        </Button>
                      </div>
                    </div>

                    {/* 나의 카테고리 섹션 */}
                    {categories.length > 0 && (
                      <>
                        <Divider className="my-6" />
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <TagsOutlined className="text-[18px] text-[#69C9D0]" />
                            <label className="text-[16px] font-semibold text-[#262626] m-0">
                              나의 카테고리
                            </label>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {categories.map((category) => {
                              return (
                                <Tag
                                  key={category}
                                  className="inline-flex items-center justify-center border-none px-3 py-1 rounded-2xl text-[12px] md:text-[14px] font-medium m-0"
                                  style={{
                                    backgroundColor:
                                      CATEGORY_STYLES[category].backgroundColor,
                                    color: CATEGORY_STYLES[category].color,
                                  }}
                                >
                                  {CATEGORY_MAPPING[category]}
                                  <CloseOutlined
                                    className="text-[8px] ml-1.5 cursor-pointer p-0.5 rounded-full"
                                    style={{
                                      color: CATEGORY_STYLES[category].color,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteCategories(category, "tiktok");
                                    }}
                                  />
                                </Tag>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </Card>

                  {/* 크리에이터의 틱톡 정보 */}
                  <div>
                    {/* 프로필 */}
                    <div>
                      <Card className="mb-6 rounded-xl border border-[#E8E8E8]">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-12">
                          <Avatar
                            src={influencerInfo.profile_img}
                            className="!w-[72px] !h-[72px] md:!w-[96px] md:!h-[96px] border-4 border-[#F0F0F0]"
                          />

                          <div className="min-w-0">
                            <div className="mb-2 flex flex-wrap gap-1">
                              {categories.map((category, idx) => (
                                <Tag
                                  key={idx}
                                  className="border-none px-2 py-0.5 text-[11px] md:text-[12px] font-medium rounded-lg m-0"
                                  style={
                                    CATEGORY_STYLES[category] ||
                                    CATEGORY_STYLES.etc
                                  }
                                >
                                  {CATEGORY_MAPPING[category] || category}
                                </Tag>
                              ))}
                            </div>

                            <div className="flex items-center gap-2">
                              <p className="m-0 text-[18px] md:text-[22px] font-bold leading-tight break-words">
                                {influencerInfo.nickname || "-"}
                              </p>
                            </div>

                            <p className="text-[#8C8C8C] m-0 text-[12px] md:text-[13px] break-words">
                              @{influencerInfo.unique_id || "-"}
                            </p>

                            {influencerInfo.bio && (
                              <p className="text-[#595959] text-[12px] md:text-[13px] mt-2 leading-relaxed break-words">
                                {influencerInfo.bio}
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>
                    </div>

                    {/* 채널 기본 통계 */}
                    <div className="mb-8">
                      <Card
                        title="채널 통계"
                        className="rounded-xl border border-[#E8E8E8]"
                        styles={{ body: { padding: "24px" } }}
                      >
                        <Row gutter={[16, 16]}>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={
                                <span className="text-[13px] text-[#8C8C8C]">
                                  팔로워
                                </span>
                              }
                              value={influencerInfo.follower_count}
                              prefix={
                                <UserOutlined className="text-[#5B8FF9] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={
                                <span className="text-[13px] text-[#8C8C8C]">
                                  총 좋아요
                                </span>
                              }
                              value={influencerInfo.like_count}
                              prefix={
                                <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={
                                <span className="text-[13px] text-[#8C8C8C]">
                                  총 영상
                                </span>
                              }
                              value={influencerInfo.video_count}
                              prefix={
                                <VideoCameraOutlined className="text-[#722ED1] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {value}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={renderLabelWithTooltip(
                                "평균 조회수",
                                getAverageMetricTooltip("조회수")
                              )}
                              value={influencerInfo.average_views || 0}
                              prefix={
                                <EyeOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={renderLabelWithTooltip(
                                "평균 좋아요",
                                getAverageMetricTooltip("좋아요")
                              )}
                              value={influencerInfo.average_likes || 0}
                              prefix={
                                <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={renderLabelWithTooltip(
                                "평균 댓글",
                                getAverageMetricTooltip("댓글")
                              )}
                              value={influencerInfo.average_comments || 0}
                              prefix={
                                <CommentOutlined className="text-[#1890FF] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={renderLabelWithTooltip(
                                "평균 저장",
                                getAverageMetricTooltip("저장")
                              )}
                              value={influencerInfo.average_collects || 0}
                              prefix={
                                <BookOutlined className="text-[#FAAD14] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={renderLabelWithTooltip(
                                "평균 공유",
                                getAverageMetricTooltip("공유")
                              )}
                              value={influencerInfo.average_shares || 0}
                              prefix={
                                <ShareAltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold">
                                  {formatCnt(value)}
                                </span>
                              )}
                            />
                          </Col>
                          <Col xs={12} lg={8}>
                            <Statistic
                              title={
                                <span className="text-[13px] text-[#8C8C8C]">
                                  참여율
                                </span>
                              }
                              value={calculateEngagementRate(tiktokData)}
                              prefix={
                                <ThunderboltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                              }
                              formatter={(value) => (
                                <span className="text-[22px] md:text-[24px] font-semibold text-[#52C41A]">
                                  {value}%
                                </span>
                              )}
                            />
                          </Col>
                        </Row>
                      </Card>
                    </div>

                    {/* 주간 추이 */}
                    <div className="mb-8">
                      <SectionTitle>주간 추이</SectionTitle>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard title="팔로워" data={trends.followers} />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard title="좋아요" data={trends.likes} />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard title="영상 조회수" data={trends.videos} />
                        </Col>
                      </Row>
                    </div>

                    {/* 영상 인게이지먼트 추이 */}
                    <div className="mb-8">
                      <SectionTitle>영상 인게이지먼트 추이</SectionTitle>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title={renderLabelWithTooltip(
                              "평균 조회수",
                              getAverageMetricTooltip("조회수")
                            )}
                            titleText="평균 조회수"
                            data={trends.avgViews}
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title="고유 영상 조회수"
                            data={mtrends.uniqueVideoViews}
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title={renderLabelWithTooltip(
                              "평균 좋아요",
                              getAverageMetricTooltip("좋아요")
                            )}
                            titleText="평균 좋아요"
                            data={trends.avgLikes}
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title={renderLabelWithTooltip(
                              "평균 댓글",
                              getAverageMetricTooltip("댓글")
                            )}
                            titleText="평균 댓글"
                            data={trends.avgComments}
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title={renderLabelWithTooltip(
                              "평균 저장",
                              getAverageMetricTooltip("저장")
                            )}
                            titleText="평균 저장"
                            data={trends.avgCollects}
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title={renderLabelWithTooltip(
                              "평균 공유",
                              getAverageMetricTooltip("공유")
                            )}
                            titleText="평균 공유"
                            data={trends.avgShares}
                          />
                        </Col>
                        <Col xs={24} sm={12} lg={8}>
                          <TrendCard
                            title="일일 총 팔로워"
                            data={mtrends.dailyTotalFollowers}
                          />
                        </Col>
                      </Row>
                    </div>

                    {metricsData.length > 0 && (
                      <div className="mb-8">
                        <SectionTitle>틱톡 사용자 반응 추이</SectionTitle>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} md={12}>
                            <TrendCard
                              title="링크 클릭 수"
                              data={engagedAudienceTrends}
                              showAllTicks
                            />
                          </Col>
                          <Col xs={24} md={12}>
                            <TrendCard
                              title="유효 사용자 수"
                              data={appDownloadClickTrends}
                              showAllTicks
                            />
                          </Col>
                        </Row>
                      </div>
                    )}

                    {hasAudienceData && (
                      <div className="mb-8">
                        <SectionTitle>오디언스 정보</SectionTitle>
                        <Router.AudienceInsights
                          influencerInfo={influencerInfo}
                        />
                      </div>
                    )}

                    {/* 최근 영상 */}
                    {recentVideos && recentVideos.length > 0 && (
                      <div>
                        <Router.RecentVideosCarousel
                          videos={recentVideos}
                          influencerId={influencerInfo.unique_id}
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <CreatorAiAnalysisPanel
                  uniqueId={influencerInfo.unique_id}
                  isVisible
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
